import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Tippy from '@tippyjs/react'

import { useLogout } from '../../../../../hooks/auth/useLogout'

import Icon from '../../../../ui/Icon'
import Avatar from '../../../../ui/Avatar'
import { Button } from '../../../../buttons/Button'

import { useApp } from '../../../../../contexts/AppContext'
import { useAuth } from '../../../../../contexts/AuthContext'
import { useProfileContext } from '../../../../../contexts/ProfileContext'
import { getFromToken, JwtKeys, RoleType } from '../../../../../utils/jwtUtils'
import { displayName } from '../../../../../utils/profile/profile'
import { SKILL_LEVEL_VALUES } from '../../../../../types/user/userProfile'
import {
  ACCOUNT_NAV_SECTIONS,
  ADMIN_NAV_ITEMS,
  BACK_TO_DASHBOARD_NAV_ITEM,
  HELP_NAV_ITEMS,
  OPERATIONS_NAV_ITEMS,
  SETUP_NAV_ITEMS,
  USER_NAV_ITEMS,
} from '../../../../../constants/navigation'
import {
  FeatureFlags,
  isFeatureEnabled,
} from '../../../../../constants/featureFlags'

type Section = {
  label?: string
  items: NavItem[]
}

type NavItem = {
  path: string
  label: string
  icon: string
  mobileOnly?: boolean
}

interface SideNavContentProps {
  onNavigate: (path: string) => void
}

const SideNavContent = ({ onNavigate }: SideNavContentProps) => {
  const { pathname } = useLocation()
  const { t } = useTranslation(['nav', 'account'])
  const { authInfo } = useAuth()
  const { profile, pictureUrl } = useProfileContext()
  const { roles } = useApp()
  const { logout } = useLogout()

  const isManager = roles.includes(RoleType.MANAGER)
  const isAdmin = roles.includes(RoleType.ADMIN)
  const partnersEnabled = isFeatureEnabled(FeatureFlags.FF_PARTNERS)
  const membershipsEnabled = isFeatureEnabled(FeatureFlags.FF_MEMBERSHIPS)

  const featureEnabled = (path: string) => {
    if (path === '/partners') {
      return partnersEnabled
    }

    if (path === '/passes' || path === '/m/passes') {
      return membershipsEnabled
    }

    return true
  }

  const translateItems = (
    items: readonly {
      path: string
      labelKey: string
      icon: string
      mobileOnly?: boolean
    }[]
  ): NavItem[] =>
    items
      .filter(item => featureEnabled(item.path))
      .map(item => ({
        ...item,
        label: t(item.labelKey),
      }))

  // Until the email is confirmed the only place the user can go is the booking page (which asks them to confirm).
  const emailPending = profile?.emailConfirmed === false
  const user = translateItems(
    emailPending ? USER_NAV_ITEMS.filter(item => item.path === '/booking') : USER_NAV_ITEMS
  )
  const operations = translateItems(OPERATIONS_NAV_ITEMS)
  const setup = translateItems(SETUP_NAV_ITEMS)
  const admin = translateItems(ADMIN_NAV_ITEMS)
  const help = translateItems(HELP_NAV_ITEMS)

  const isAccountPage = pathname.startsWith('/account')
  const sections: Section[] = isAccountPage
    ? [
        {
          items: [
            {
              ...BACK_TO_DASHBOARD_NAV_ITEM,
              label: t(BACK_TO_DASHBOARD_NAV_ITEM.labelKey),
            },
          ],
        },
        ...ACCOUNT_NAV_SECTIONS.map(section => ({
          label: t(section.labelKey),
          items: translateItems(section.items),
        })),
      ]
    : [
        ...(isAdmin
          ? [
              { label: t('side-bar.section.play'), items: user },
              { label: t('side-bar.section.operations'), items: operations },
              { label: t('side-bar.section.setup'), items: setup },
              { label: t('side-bar.section.administration'), items: admin },
            ]
          : isManager
            ? [
                { label: t('side-bar.section.play'), items: user },
                { label: t('side-bar.section.operations'), items: operations },
                { label: t('side-bar.section.setup'), items: setup },
              ]
            : [{ items: user }]),
        { items: help },
      ]

  const rank = profile?.skillLevel
    ? t('account:account.profile.skill-level.value', {
        level: SKILL_LEVEL_VALUES[profile.skillLevel],
      })
    : undefined
  const userEmail = getFromToken(authInfo?.accessToken ?? '', JwtKeys.EMAIL)
  const fullName = displayName(
    profile?.firstName,
    undefined,
    userEmail ?? ''
  )

  /** A link stays highlighted on its sub-pages too (editing a court highlights "Courts"). */
  const isActive = (path: string) =>
    pathname === path ||
    pathname.startsWith(`${path}/`) ||
    (pathname === '/account' && path === '/account/profile') ||
    (pathname === '/account/terms-and-conditions' && path === '/account/terms')

  const handleNavigate = (path: string) => {
    onNavigate(path)
  }

  return (
    <>
      <div className="ph-sections">
        {sections.map(({ label: sectionLabel, items }, index) => (
          <div className="ph-side-section" key={index}>
            {sectionLabel && (
              <div className="ph-side-label">{sectionLabel}</div>
            )}

            {items.map(({ path, label, icon, mobileOnly }) => (
              <Button
                key={path}
                variant="ghost"
                className={`ph-side-link ${mobileOnly ? 'd-lg-none' : ''} ${isActive(path) ? 'active' : ''}`}
                icon={
                  <span className="icon">
                    <Icon name={icon} size={17} />
                  </span>
                }
                label={label}
                onClick={() => handleNavigate(path)}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="ph-side-user d-lg-none">
        <div className="user-info">
          <Avatar name={fullName} image={pictureUrl} />

          <div className="user-details">
            <div className="name">{fullName}</div>

            {rank && <div className="rank mono">{rank}</div>}
          </div>
        </div>

        <Tippy
          content={t('side-bar.sign-out')}
          placement="top"
          className="ph-tooltip"
        >
          <Button
            variant="ghost"
            className="ph-iconbtn"
            icon={<Icon name="logout" size={15} />}
            iconOnly
            label={t('side-bar.sign-out')}
            onClick={logout}
          />
        </Tippy>
      </div>
    </>
  )
}

export default SideNavContent
